import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Add as AddIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';
import Can from '../../../components/Can';
import type { CrmKanbanColumna, CrmOportunidadListItem } from '../../../types/crm';
import type { CrmRealtimeFuente } from '../../../types/crmRealtime';
import { formatCurrency } from '../../../utils/formatters';
import CrmDealCard from './CrmDealCard';

export interface CrmKanbanColumnProps {
  columna: CrmKanbanColumna;
  onDealClick: (deal: CrmOportunidadListItem) => void;
  onAddClick?: (etapaId: number) => void;
  showPipelineLabel?: boolean;
  pipelineNombreById?: Record<number, string>;
  highlightedDealIds?: Set<number>;
  dealFuentes?: Map<number, CrmRealtimeFuente | string>;
  flash?: boolean;
}

const CrmKanbanColumn: React.FC<CrmKanbanColumnProps> = ({
  columna,
  onDealClick,
  onAddClick,
  showPipelineLabel = false,
  pipelineNombreById,
  highlightedDealIds,
  dealFuentes,
  flash = false,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `etapa-${columna.etapa.id}`,
    data: { etapaId: columna.etapa.id },
  });

  const color = columna.etapa.color || '#1976d2';
  const isWon = Boolean(columna.etapa.esCierreGanado);
  const isLost = Boolean(columna.etapa.esCierrePerdido);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: { xs: 240, sm: 280 },
        maxWidth: { xs: 280, sm: 320 },
        flex: { xs: '0 0 240px', sm: '0 0 280px' },
        bgcolor: isOver ? 'rgba(25,118,210,0.08)' : flash ? 'rgba(46,125,50,0.08)' : '#f0f2f5',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 'calc(100vh - 380px)', sm: 'calc(100vh - 320px)' },
        border: isOver ? '2px solid' : '1px solid',
        borderColor: isOver ? 'primary.main' : flash ? 'success.main' : 'transparent',
        transition: 'background-color 0.35s ease, border-color 0.35s ease',
        ...(isWon && {
          bgcolor: isOver ? 'rgba(46,125,50,0.12)' : 'rgba(46,125,50,0.06)',
          borderColor: isOver ? 'success.main' : 'rgba(46,125,50,0.25)',
        }),
        ...(isLost && {
          bgcolor: isOver ? 'rgba(211,47,47,0.1)' : 'rgba(211,47,47,0.04)',
        }),
      }}
    >
      <Box
        sx={{
          mx: -1.5,
          mt: -1.5,
          mb: 1.25,
          px: 1.5,
          py: 1,
          borderRadius: '8px 8px 0 0',
          bgcolor: `${color}18`,
          borderBottom: `3px solid ${color}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            {isWon ? (
              <TrophyIcon sx={{ fontSize: 16, color: '#2e7d32', flexShrink: 0 }} />
            ) : (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: color,
                }}
              />
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {columna.etapa.nombre}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                flexShrink: 0,
                bgcolor: 'rgba(0,0,0,0.06)',
                px: 0.75,
                py: 0.15,
                borderRadius: 1,
                fontWeight: 600,
              }}
            >
              {columna.oportunidades.length}
            </Typography>
          </Stack>
          {onAddClick && !isWon && !isLost && (
            <Can permiso="crm.oportunidades.nuevo">
              <IconButton
                size="small"
                aria-label={`Nueva oportunidad en ${columna.etapa.nombre}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddClick(columna.etapa.id);
                }}
                sx={{ p: 0.35, flexShrink: 0 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Can>
          )}
        </Stack>
        <Typography variant="caption" sx={{ fontWeight: 700, color: isWon ? '#2e7d32' : 'text.secondary', mt: 0.5, display: 'block' }}>
          {formatCurrency(columna.montoTotal)}
        </Typography>
      </Box>
      <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
        {columna.oportunidades.map((deal) => (
          <CrmDealCard
            key={deal.id}
            deal={deal}
            onClick={() => onDealClick(deal)}
            isNew={highlightedDealIds?.has(deal.id)}
            fuente={dealFuentes?.get(deal.id)}
            pipelineLabel={
              showPipelineLabel ? pipelineNombreById?.[deal.pipelineId] ?? null : null
            }
          />
        ))}
        {columna.oportunidades.length === 0 && isWon && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2, opacity: 0.7 }}>
            Arrastrá acá para cerrar ganada
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CrmKanbanColumn;
